import { DatePipe } from "@angular/common";
import { AfterViewChecked, Component, ElementRef, HostListener, Input, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ColDef, GridApi, GridOptions, GridReadyEvent, IAggFunc, IRowNode, MenuItemDef, RowNode, ValueFormatterParams } from "ag-grid-community";
import { ChartConfiguration } from "chart.js";
import moment from "moment";
import { BaseChartDirective } from "ng2-charts";
import { Subscription } from 'rxjs';
import { NavigationService } from "src/app/navigation/navigation.service";
import { AgContextMenuService } from "src/app/reusables/services/ag-context-menu.service";
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from "src/app/reusables/services/cv-configuration.service";
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleHelperService } from "src/app/reusables/services/module-helper.service";
import { ModuleThemesService } from "src/app/reusables/services/module-themes.service";
import SimpleContextMenuRenderer from "src/shared/ag-grid-components/simple-context-menu";
import { ErrorCode } from "src/shared/models/error-code.model";
import Metric, { NumericType } from 'src/shared/models/metric';
import MetricData from 'src/shared/models/metric-data';
import ModuleDataGroupItem from "src/shared/models/module-data-group-item";
import { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerMenuItem, ModuleManagerMenuItemPlacement, ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { ModuleManagerMetricInfo } from "src/shared/models/module-manager/module-manager-settings.models";
import { AgentStateDetailComponent } from "../../modals/agent-state-detail/agent-state-detail.component";
import { TasksModalComponent } from "../../modals/tasks-modal/tasks-modal.component";
import { WidgetThresholdsModalComponent } from "../../modals/widget-thresholds-modal/widget-thresholds-modal.component";
import { ThresholdItem } from "../../modals/widget-thresholds-modal/widget-thresholds-modal.model";
import { WidgetThresholdsModalService } from "../../modals/widget-thresholds-modal/widget-thresholds-modal.service";
import { CurrentAgentStatesService } from "../current-agent-states/current-agent-states.service";
import { BreakdownItem, IntervalModuleData, IntervalModuleDisplay, IntervalState, IntervalTableState, SingleMetricItem, TableViewMode, TotalItem, PinnedRowData } from './interval-combo.models';
import { IntervalComboService } from './interval-combo.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ObjectivesDetailsService } from '../../../reusables/objectives-details/objectives-details.service';
import { AgentStateDetailCXOneComponent } from "../../modals/agent-state-detail-cxone/agent-state-detail-cxone.component";
import { SuggestCoachingModalComponent } from "../../modals/suggest-coaching-modal/suggest-coaching-modal.component";
import { ContextMenuAwardsDialogComponent } from "../../awards/context-menu-awards-dialog/context-menu-awards-dialog.component";

@Component({
    selector: 'interval-combo',
    templateUrl: './interval-combo.component.html',
    styleUrls: ['./interval-combo.component.scss'],
    encapsulation: ViewEncapsulation.None,
})

export class IntervalComboComponent implements OnInit, AfterViewChecked {
    // This is the component controller for the Interval module. The Interval module has both a table and a chart view.
    // this component contains all methods and variables necessary for both the chart and the table component.
    // The Table View has three different modes, depending on the module settings.
    // These Table Views are:
    // Single Metric View: Module settings have only selected one metric. The first column displays the configured groups, and subsequent columns display the metric value for that column's interval.
    // Multiple Metric View: Module settings have selected more than one metric. The first column indicates the interval, each subsequent column displays each metric total for that interval. Groups are not displayed in this view.
    // Breakdown View: Module settings have enabled Breakdown View. The number of metrics doesn't matter. This view has expandable/collapsible groups.
    // Breakdown Interval View: The first column displays groups, subsequent columns display metric values. The rows are grouped by interval. Interval totals are displayed in the row group headers.
    // Breakdown Group View: Rows are grouped by Group and totals for the group are displayed on the group row with intervals in the dropdown.

    @ViewChild('intervalWidgetGrid') agGrid: AgGridAngular;
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @Input() metrics: ModuleManagerMetricInfo[] = [];
    @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;

    // Chart variables
    legendItemsHidden = new Map();
    data: IntervalModuleData = null;
    dataLoading = false;
    dataLoadSuccess = false;
    _localizationsLoaded = false;
    themeMenuOptions = [];
    chartConfig = null;
    modalRef: NgbModalRef = null;
    isAgentStateDetailModalOpen = false;

    //Chart.js Stuff
    //Chart module state
    chartState = {
        //State object properties
        legendPosition: 'top',
        themeIndex: 0,
        chartType: 'line',
        name: 'chartState',
        autoLoad: true,
        showLegendPosition: true
    };
    chartType = (this.chartState.chartType != null) ? this.chartState.chartType : 'line';
    chartLegendPosition = (this.chartState.legendPosition != null) ? this.chartState.legendPosition : 'top';
    showLegendPosition = this.chartState.showLegendPosition ?? true;
    chartPadding = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    };
    chartAxisShowStates = {};
    chartMaximumRotation = 90; //90 degrees is the default.
    firstAnimateComplete = false;

    //Color theme handling
    currentThemeIndex = (this.chartState.themeIndex != null) ? this.chartState.themeIndex : 0;
    currentTheme = null;
    isCXOne = false;

    // Subscriptions
    subscriptions: Subscription[] = [];

    backgroundcolor = {
        id: 'custom_canvas_background_color',
        beforeDraw: (chart) => {
            const ctx = chart.canvas.getContext('2d');
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = '#F5F8FA';
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
        }
    };

    // table variables
    public filterValue = ""; // Content of the search bar.
    public isNoData = false; // Boolean indicating the data load was empty.
    public isNoMetric = false; // Boolean indicating that no metrics were selected.
    public isTooMuchData = false; // Boolean indicating that breakdown or single views tried to load too much data (more than 3500 rows).
    public isDateRangeTooWideIntraday = false; // Boolean indicating that the Time Range or Interval Settings are invalid with the selected Date Range
    public searchOpen = false; // Used in ng-if to show/hide the search bar.
    public moduleDisplay: IntervalModuleDisplay = IntervalModuleDisplay.Table; // This module can display the ag-grid table, a ChartJS chart, or both.
    public oldDisplay: IntervalModuleDisplay = IntervalModuleDisplay.None; // Used to store the previous display mode when switching between views.
    public gridOptions = null;
    public showTotalsRow = false; // Saved state value indicating to show or hide the totals row.
    public showTotal = true; // Saved state value indicating to show totals as totals or averages.
    public totalsRowData: PinnedRowData; // Since the totals row can be toggled at any time, its data is stored here to avoid rebuilding it.
    public avgsRowData: PinnedRowData; // Stores avgs row data so it can be toggled without rebuilding

    //For cxone search placeholder localization
    searchPlaceholder = "";

    //Object for ag grid localization
    localeText = {};
    totalsText = "";
    cxoneTotalText = "";
    avgsText = "";

    private isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"

    public aggFuncs:
        {[key: string]: IAggFunc} = {
            /** The aggregation feature of Ag-Grid is used to group columns by the interval value.
                The aggFunc receives an array of values returned by valueGetter.*/
            intervalAgg: params => params?.values?.[0]?.interval,

            /** The aggregation function is the total for all the groups is shown in the expandable row groups
                This will return the same "total", even when there is row filtering. */
            breakdownAgg: params => {
                if (Array.isArray(params?.values)) {
                    const mdIntervalTotal = params.values.find(n => n?.intervalTotal != null);
                    if (mdIntervalTotal) {
                        return mdIntervalTotal.intervalTotal;
                    }
                }
                return null;
            },

            breakdownGroupAgg: params => {
                if (Array.isArray(params?.values)) {
                    const mdIntervalTotal = params.values.find(n => n?.groupTotal != null);
                    if (mdIntervalTotal) {
                        return mdIntervalTotal.groupTotal;
                    }
                }
                return null;
            }
        };

    private cancelRefresh: () => void; // This function is used to resolve the cancellerRefresh promise in loadData()
    private currentDataKey: string; // Indicates the kind of data the grid is built for. Grid options are specific to the settings. For a Single Metric, the grid cares about the date periods. For Breakdown and Multiple, the grid cares about the number and order of metrics.
    private currentTableView: TableViewMode = null; // null means "Uninitialized". Indicates the currently constructed grid view.
    private gridState: IntervalTableState; // The object that is saved to or restored from state.
    private gridStateLoaded = false; // Indicates whether the grid state has been loaded from state.
    private isFirstLoad = true; // Used to log whether the data load is the first load since the module loaded.
    private isLoadingData = false; // Prevent non-force refreshes if another refresh is already happening. Also used to disable click on the "Refresh" action in the module menu.
    private localizationLoaded = false; // If false after the first data load, delay the construction of grids. 
    private stateSaveTimeout: any; // For debouncing calls to queueStateSave().
    private resizeColsSaveStateTimeout: any;
    private focusOnSearchTimeout: any;
    private parentVisible = false;
    private agentStatesContextMenuSubscribed = false;
    private displayCheckItems = [];
    private tableDisplayItems = [];
    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; }
    private metricThresholds: Record<string, ThresholdItem[]> = {};
    metricNames = new Map<string, string>();
    private animateUpdate = true;
    private hideShowLegendClicked = false;
    public zoom: any = {
        max: 2.5,
        min: 1,
        cur: 1,
        baseTicks: 5,
    };

    tooltipLegendItems = new Map<number, string>();

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: IntervalComboService,
        private moduleThemesService: ModuleThemesService,
        public config: CvConfigurationService,
        private navigationService: NavigationService,
        private agContextMenuService: AgContextMenuService,
        private ngbModal: NgbModal,
        private agentStateService: CurrentAgentStatesService,
        private datePipe: DatePipe,
        private thresholdService: WidgetThresholdsModalService,
        private moduleHelperService: ModuleHelperService,
        private objectivesDetailsService: ObjectivesDetailsService,
    ) {
        this.isCXOne = config.isCxOne;
        this.chartType = (this.chartState.chartType != null) ? this.chartState.chartType : 'line';

    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    @HostListener('wheel', ['$event']) onMouseWheel(event: WheelEvent) {
        if (this.shouldHandleZoom(event)) {
            this.handleZoom(event.deltaY);
            event.preventDefault(); // Prevent the default behavior only when handling zoom
        }
    };
    // ensures the onMouseWheel override only occurs when the cursor is over the chart area
    shouldHandleZoom(event) {
        const targetElement = document.elementFromPoint(event.clientX, event.clientY);
        const chart = this.chartObject?.chart.canvas.parentElement.parentElement;
        return chart?.contains(targetElement);
    }

    /**
     * Generates a string key name that can be used to compare if the moduleData and the gridOptions are compatible.
     * For example:
     * Two single-metric data loads can refer to different date ranges or a new interval has appeared from load to load and the columns will not match.
     * A breakdown or multiple metric load could have swapped metric order or changed the metrics.
     *
     * @param moduleData
     */
    private static generateDataKey(moduleData: IntervalModuleData) {
        const gridType = moduleData?.ViewMode;
        let key = "";
        if (gridType === TableViewMode.SingleMetric) {
            // For Single Metric grids, the metric doesn't matter.
            key = "Single";
            moduleData.IntervalOrder?.forEach(intervalStr => key += `_${intervalStr}`);
            return key;
        } else if (gridType === TableViewMode.MultipleMetrics) {
            // Multiple Metrics and Breakdown don't care about the intervals, but they care about the metrics and their order.
            key = "Multiple";
        } else if (gridType === TableViewMode.Breakdown) {
            key = moduleData.ListByInterval ? "BreakdownInterval" : "BreakdownGroup";
        }
        moduleData?.Metrics?.forEach(metric => key += `_${metric.MetricID}`);
        return key;
    }

    /**
     * Configures module manager and loads localizations
     */
    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        // Localizations:
        const localizationPromises = [
            this.cvLocalizationService.GetSection("Interval_Module"), // getCachedText will be used
            this.cvLocalizationService.GetSection("Error_Module"),
            this.cvLocalizationService.GetSection("Common"),
            this.cvLocalizationService.GetSection("Objectives"),
        ];
        await Promise.all(localizationPromises);

        this.localeText = await this.agGridHelperService.getLocalizations();
        this.totalsText = this.cvLocalizationService.getCachedText("Interval_Module", "Totals", "Totals");
        this.cxoneTotalText = this.cvLocalizationService.getCachedText("Interval_Module", "Total", "Total");
        this.avgsText = this.cvLocalizationService.getCachedText("Common", "Averages", "Averages");

        this.moduleManager.setOptions({
            canRefresh: () => !this.isLoadingData,
            hasRefresh: true,
            onExport: (format, isCXOne) => this.service.exportData(
                this.moduleKey, format, isCXOne, this.moduleManager.container.moduleInfo.DTO.ModuleID),
            exportEnabled: () => !this.isTooMuchData && !this.isNoData && !this.isDateRangeTooWideIntraday,
            onImageExport: (isCXOne) => this.exportPNG(),
            imageExportVisible: () => this.moduleDisplay !== IntervalModuleDisplay.Table,
            onRefresh: isAutoRefresh => { return this.loadData(isAutoRefresh); },
            refreshType: ModuleManagerRefreshType.Auto,
            onSettingsApplied: () => this.loadData(false, true), // This is called when the date range selector dropdown in the top-left is changed.  Force a refresh, in case the user is rapidly changing between date ranges.
        });

        this.displayCheckItems = [
            {
                displayName: this.cvLocalizationService.getCachedText('Interval_Module', 'Show_Totals', 'Show Totals'),
                checked: this.showTotalsRow
            },
            {
                displayName: this.cvLocalizationService.getCachedText('Interval_Module', 'Show_Parent', 'Show Parent Level'),
                checked: this.parentVisible
            }
        ];

        this.tableDisplayItems = [ // items for the display menu that uses both radio buttons and checkboxes
            {
                displayName: this.getLocaleText('Show_Totals'),
                inputType: 'radio',
                value: true,
                checked: this.showTotal
            },
            {
                displayName: this.cvLocalizationService.getCachedText("Common", "None", "None"),
                inputType: 'radio',
                value: false,
                checked: this.showTotal
            },
            {
                displayName: "",
                inputType: 'divider',
            },
            {
                displayName: this.getLocaleText('Show_Parent_Level'),
                inputType: 'checkbox',
                checked: this.parentVisible
            },
        ];

        // setup ModuleManager
        this.moduleManager.showLoader = true;

        // This stores the state of the table.  Column orderings and sort state.
        // State is only applied if the stateKey matches the currentDataKey
        this.moduleManager.addState({
            name: IntervalState.Table,
            getStateData: () => this.gridState,
            onStateLoaded: stateData => {
                if (!stateData) { // No table state loaded
                    return;
                }
                this.gridState = stateData;
                this.gridStateLoaded = true;
            },
            autoLoad: true
        });

        // This stores the state of the Totals row.  The user can check/uncheck it in the module menu.  This value is persisted.
        this.moduleManager.addState({
            name: IntervalState.TotalsRow,
            getStateData: () => this.isCXOne ? this.showTotalsRow : this.showTotal,
            onStateLoaded: (state: boolean) => {
                this.restoreTotalsRowState(!!state);
                if (this.isCXOne)
                    this.displayCheckItems[0].checked = !!state;
                else {
                    this.tableDisplayItems.filter(i => i.inputType === 'radio').forEach((item) => { item.checked = !!state });
                }
            },
            autoLoad: true,
        });

        // This stores the state of the Location column renamed as Parent column for cxone.
        this.moduleManager.addState({
            name: IntervalState.ParentVisible,
            getStateData: () => this.parentVisible,
            onStateLoaded: (state: boolean) => {
                this.parentVisible = !!state;
                if(this.isCXOne)
                    this.displayCheckItems[1].checked = this.parentVisible;
                else
                    this.tableDisplayItems[3].checked = this.parentVisible;
            },
            autoLoad: true
        });

        // This stores whether to show the table, the chart, or both.  By default, the module displays the table.
        this.moduleManager.addState({
            name: IntervalState.ModuleDisplay,
            // Matching the previous incarnations of this module, for compatibility
            getStateData: () => ({ view: this.moduleDisplay }),
            onStateLoaded: (state: { view: IntervalModuleDisplay | "graph"}) => {
                switch (state?.view) {
                    // this case is added for the cutover since i refractored graph to be the string chart.
                    case "graph":
                        this.moduleDisplay = IntervalModuleDisplay.Chart;
                        break;
                    case IntervalModuleDisplay.Dual:
                    case IntervalModuleDisplay.Chart:
                    case IntervalModuleDisplay.Table:
                        this.moduleDisplay = state.view;
                        break;
                    default:
                        this.moduleDisplay = IntervalModuleDisplay.Table;
                        break;
                }
            },
            autoLoad: true,
        });

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        // chart initilization below
        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
        this.localizationLoaded = true;
        this._localizationsLoaded = true;
        this.themeMenuOptions = this.moduleThemesService.GetModuleThemesMenu();
        this.setThemeClick();
        this.moduleManager.addState({
            name: 'chartState',
            autoLoad: true,
            getStateData: () => this.chartState,
            onStateLoaded: stateData => {
                if (!stateData) {
                    return;
                }
                this.chartState = stateData;
                this.chartType = this.chartState.chartType || 'line';
                this.currentThemeIndex = (this.chartState.themeIndex != null) ? this.chartState.themeIndex : 0;
                this.currentTheme = this.moduleThemesService.ModuleThemes[this.currentThemeIndex].Colors;
                this.showLegendPosition = this.chartState.showLegendPosition ?? true;
                this.chartLegendPosition = this.chartState.legendPosition ?? 'top';
                this.applyLegendPadding(this.chartLegendPosition);
            }
        });

        // assures menu items are set on the first load
        this.resetMenuItems();
        this.metrics = this.moduleManager.moduleSettings.selectedMetrics;
    }


    /**
     * Applies the content of the `this.filterValue` to the grid's quick filter.
     * Called by the ngChange on the search input element.
     */
    public applyFilter(): void {
        this.agGrid?.api?.setGridOption('quickFilterText', this.filterValue.trim());
        if(this.searchOpen && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0){
            this.agGrid?.api?.showNoRowsOverlay();
        } else {
            this.agGrid?.api?.hideOverlay();
        }
    }

    /**
     * Clears the content of the search bar and updates the grid's filter.
     * Called by the (x) icon that appears when the `this.filterValue` has text.
     */
    public clearFilter(): void {
        this.filterValue = "";
        this.applyFilter();
    }

    /**
     * Toggle whether to show or hide the search bar
     */
    public toggleOpenSearch() {
        this.searchOpen = !this.searchOpen;
        if (this.searchOpen) {
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        } else {
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        }
    }

    /**
     * Resets the module menu items
     */
    resetMenuItems() {
        this.moduleManager.resetMenu();
        this.addGenericMenuItems();
        if (this.moduleDisplay !== IntervalModuleDisplay.Chart) {
            this.addMenuItemsTable();
        }
        if (this.moduleDisplay !== IntervalModuleDisplay.Table) {
            this.addMenuItemsChart();
        }
    }

    /**
     * Adds menu items to the module menu that are always there.
     * The module menu shows different items depending on the ModuleDisplay
     */
    addGenericMenuItems() {
        const displayItems = [];
        const options = this.moduleManager.getModuleInfo().options;
        const isBreakdown = this.moduleManager.customSettings?.BreakdownMode.getValue();

        if (this.isCXOne && this.moduleDisplay !== IntervalModuleDisplay.Chart) {
            this.moduleManager.addMenuItem({
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Thresholds", "Thresholds"),
                icon: "thresholds",
                getEnabled: () => !this.isNoMetric && !this.isNoData,
                type: ModuleManagerMenuItemType.MenuItem,
                readOnlyEnabled: this.isCXOne,
                click: () => this.openThresholdsModal(this.moduleKey),
                tooltip: this.moduleManager.container.noMetricsSelected() ?
                    this.cvLocalizationService.getCachedText("No_Metric_Message", "No metrics selected yet") : null,
            } as IModuleManagerMenuItem);
        }

        if (isBreakdown) {
            this.moduleManager.addMenuItem({
                key: "first",
                name: "Expand All",
                icon: "menu-expand",
                type: ModuleManagerMenuItemType.MenuItem,
                placement: ModuleManagerMenuItemPlacement.AfterExport,
                click: () => {
                    this.agGrid?.api.expandAll();
                },
            });
            this.moduleManager.addMenuItem({
                key: "second",
                name: "Collapse All",
                icon: "menu-collapse",
                type: ModuleManagerMenuItemType.MenuItem,
                placement: ModuleManagerMenuItemPlacement.AfterExport,
                click: () => {
                    this.agGrid?.api.collapseAll();
                },
            });
        }

        if (!options?.tableAvailable || !options?.chartAvailable)
            return; // only one view so we don't need this menu

        if (options.tableAvailable)
            displayItems.push({
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Table", "Table"),
                icon: 'tableView',
                click: () => this.changeAndSaveModuleDisplay(IntervalModuleDisplay.Table),
            });
        if (options.chartAvailable)
            displayItems.push({
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Chart", "Chart"),
                icon: 'chartView',
                click: () => this.changeAndSaveModuleDisplay(IntervalModuleDisplay.Chart),
            });
        if (options.tableAndChartAvailable)
            displayItems.push({
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Table_Chart", "Table and Chart"),
                icon: 'tableAndChartView',
                click: () => this.changeAndSaveModuleDisplay(IntervalModuleDisplay.Dual),
            });

        this.moduleManager.addMenuItem({
            name: 'Widget Display',
            localizedName: this.cvLocalizationService.getCachedText('Interval_Module', 'Widget_Display', 'Widget Display'),
            icon: 'widgetType',
            type: 'subMenu',
            readOnlyEnabled: this.isCXOne,
            getEnabled: () => this.dataLoadSuccess && !this.isNoMetric && !this.isNoData,
            subItems: displayItems
        } as ModuleManagerSubMenu);
    }

    /** Set visibility of a column when the user toggles a column in columns menu */
    toggleColumnVisible(i) {
        //Getting the column definition
        const column = this.agGrid?.api['columnModel'].gridColumns[i];
        const isHidden = !column.visible;
        //Hiding or showing the column
        this.agGrid?.api.setColumnVisible(column.colId, isHidden);
        //Updating the grid
        this.tableStateChanged();
    }

    /**
     * Change whether the module displays just the grid, just the chart, or both.
     *
     * @param moduleDisplay
     */
    changeAndSaveModuleDisplay(moduleDisplay: IntervalModuleDisplay) {
        if (this.moduleDisplay === moduleDisplay) { // going to the same display view as currently shown
            return;
        }
        this.zoom.cur = 1;

        this.oldDisplay = this.moduleDisplay;
        this.moduleDisplay = moduleDisplay;

        // this section maps when tables should be created or destroyed
        // and when charts should be created (chart destruction is handled by the ng2-chart wrapper)
        // depending on the view change
        if (this.moduleDisplay === IntervalModuleDisplay.Chart && this.oldDisplay === IntervalModuleDisplay.Dual) {
            this.destroyGrid();
        }
        if (this.moduleDisplay === IntervalModuleDisplay.Table && this.oldDisplay === IntervalModuleDisplay.Dual) {
            this.resizeColsSaveState();
        }
        if (this.moduleDisplay === IntervalModuleDisplay.Dual && this.oldDisplay === IntervalModuleDisplay.Chart) {
            this.createOrUpdateGrid(this.data);
        }
        if (this.moduleDisplay === IntervalModuleDisplay.Table && this.oldDisplay === IntervalModuleDisplay.Chart) {
            this.createOrUpdateGrid(this.data);
            this.resizeColsSaveState();
        }
        if (this.moduleDisplay === IntervalModuleDisplay.Dual && this.oldDisplay === IntervalModuleDisplay.Table) {
            this.createChart();
        }
        if (this.moduleDisplay === IntervalModuleDisplay.Chart && this.oldDisplay === IntervalModuleDisplay.Table) {
            this.destroyGrid();
            this.createChart();
        }
        this.handleZoom();

        this.resetMenuItems();

        this.moduleManager.saveState(IntervalState.ModuleDisplay);
        this.moduleManager.requestPublishNeeded();

    }

    resizeColsSaveState() {
        if (this.resizeColsSaveStateTimeout)
            clearTimeout(this.resizeColsSaveStateTimeout);

        this.resizeColsSaveStateTimeout = setTimeout(() => { // timeout needed to ensure the grid is fully rendered before resizing (onGridReady must run before api's is available)
            this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api);
            this.tableStateChanged();
        });
    }

    /**
     * Change input focus to the search bar.
     */
    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        if (this.focusOnSearchTimeout)
            clearTimeout(this.focusOnSearchTimeout);

        this.focusOnSearchTimeout = setTimeout(() => {
            if (this.config.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            } else {
                this.searchBox?.nativeElement?.focus();
            }
        }, 200);
    }

    displayData(data) {
        this.onDataUpdated(data);
        if (this.moduleDisplay === IntervalModuleDisplay.Table) {
        }
        if (this.moduleDisplay === IntervalModuleDisplay.Chart) {
            this.createChart(); // Data loaded, create the chart
            this.moduleManager.showLoader = false;
        }
        if (this.moduleDisplay === IntervalModuleDisplay.Dual) {
            this.createChart();
        }
    }

    /**
     * Load Interval data and update the module state.
     */
    async loadData(isAutoRefresh: boolean, cancelAndForceRefresh: boolean = false) {
        if (this.isLoadingData && !cancelAndForceRefresh) {
            return;
        }
        this.isLoadingData = true;

        // If we didn't return early, cancel the previous data load.
        if (this.cancelRefresh) {
            this.cancelRefresh();
        }
        const cancellerPromise: Promise<void> = new Promise(resolve => this.cancelRefresh = () => resolve());

        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }

        try {
            const data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad, cancellerPromise);
            try {
                this.metricThresholds = await this.thresholdService.loadWidgetThresholds(this.moduleKey);
            } catch (e) {
                console.error("Exception in loadWidgetThresholds", e);
                this.metricThresholds = {};
            }
            this.isNoMetric = this.moduleManager.container.noMetricsSelected();
            this.dataLoading = false;
            this.dataLoadSuccess = true;
            this.cancelRefresh = null;
            this.isLoadingData = false;
            this.data = data;
            this.displayData(data);
            if (!this.isNoMetric && !this.isDateRangeTooWideIntraday)
                this.moduleManager.setAutoRefreshReady();
            this.resetMenuItems();
            this.moduleManager.container.showDateRange = true;
        } catch (e) {
            // If the exception is *not* a canceled refresh, have moduleManager handle the error.
            // A canceled refresh isn't something to be concerned about, since another refresh is happening.
            if (e?.code === DOMException.ABORT_ERR || e?.statusText === 'abort') {
                return;
            }
            console.error("Exception in Interval Module loadData", e);
            this.moduleManager.handleLoadError(e);
            this.isLoadingData = false;
        }
        finally {
            this.isFirstLoad = false;
            this.gridStateLoaded = false;
        }
    }

    /**
     * This function checks that the data loaded fine, and it sets isNoData or isTooMuchData appropriately.
     * It calls createOrUpdateGrid() if data loaded fine.
     *
     * @param moduleData
     */
    onDataUpdated(moduleData: IntervalModuleData): void {
        // This will be called by ngInit after localizations are loaded
        if (!this.localizationLoaded) {
            return;
        }
        this.moduleManager.showLoader = false;
        this.updateModuleNameSuffix(moduleData);

        if (moduleData?.TooMuchData) {
            return this.setTooMuchData();
        } else {
            this.isTooMuchData = false;
        }


        if (moduleData?.DateRangeTooWideIntraday) {
            return this.setDateRangeTooWideIntraday();
        } else {
            this.isDateRangeTooWideIntraday = false;
        }

        const gridType = moduleData?.ViewMode;
        if (this.isCXOne) {
            if (!moduleData.BreakdownItems) moduleData.BreakdownItems = [];
            this.isNoData = false;
        }
        if (gridType == null || !moduleData.IntervalTotals ||
            (gridType === TableViewMode.MultipleMetrics && !moduleData.GroupTotals && !moduleData.Totals) ||
            (gridType === TableViewMode.SingleMetric && !moduleData.SingleMetricItems) ||
            (gridType === TableViewMode.Breakdown && (!moduleData.BreakdownItems || !moduleData.GroupTotals && !moduleData.Totals))) {
            return this.setNoData();
        } else {
            this.isNoData = false;
        }

        this.createOrUpdateGrid(moduleData);
    }
    
    /**
     *  Method for toggling the visibility of legends
     */
    updateChartLegend() {
        if (this.chartObject) {
            requestAnimationFrame(() => {
                this.chartObject.chart.options.plugins.legend.display = this.showLegendPosition;
                this.chartObject.chart.update();
            });
        }
    }

    /**
     * Sets state indicating that there was no data and destroy the grid.
     */
    setNoData(): void {
        this.isNoData = true;
        this.destroyGrid();
    }

    /**
     * Set state boolean indicating that the data load was too large and destroy the grid.
     */
    setTooMuchData(): void {
        this.isTooMuchData = true;
        this.destroyGrid();
    }

    setDateRangeTooWideIntraday(): void {
        this.isDateRangeTooWideIntraday = true;
        this.destroyGrid();
    }

    /**
     * Determine if the grid needs to be reconstructed or reused. If reused, it just updates the data.
     *
     * @param moduleData
     */
    async createOrUpdateGrid(moduleData: IntervalModuleData) {
        if (!this.localizationLoaded) {
            return;
        }
        if (this.moduleDisplay === IntervalModuleDisplay.Chart) {
            this.currentDataKey = null;
            return;
        }
        let gridOptions;
        const dataKey = IntervalComboComponent.generateDataKey(moduleData);
        const dataViewMode = moduleData?.ViewMode;
        if (this.currentDataKey !== dataKey || this.currentTableView !== dataViewMode) {
            switch (dataViewMode) {
                case TableViewMode.MultipleMetrics:
                    gridOptions = this.createMultipleMetricsGrid(moduleData);
                    break;
                case TableViewMode.SingleMetric:
                    gridOptions = this.createSingleMetricGrid(moduleData);
                    break;
                case TableViewMode.Breakdown:
                    gridOptions = moduleData.ListByInterval ?
                        this.createBreakdownIntervalsGrid(moduleData) : this.createBreakdownGroupsGrid(moduleData);
                    break;
            }
            this.gridOptions = null;
            setTimeout(() => { this.gridOptions = gridOptions; }, 0);
            await this.handleSubscriptions();

            this.currentTableView = dataViewMode;
            this.currentDataKey = dataKey;
        } else {
            this.updateGridData(moduleData, dataViewMode);
        }
    }

    async handleSubscriptions() {
        // Wire context menus
        if (this.agentStatesContextMenuSubscribed) {
            this.subscriptions.forEach(sub => {
                sub.unsubscribe();
            });
            this.agentStatesContextMenuSubscribed = false;
        }
        this.subscriptions.push(this.agContextMenuService.agentDetailsClicked.subscribe( async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            const response = await this.agentStateService.loadCurrentAgentStatesByGroupID(params.moduleKey, params.groupData.groupID, this.isCXOne);
            this.openAgentStateDetail(params.moduleKey, params.groupData.groupID, response);
        }));

        this.subscriptions.push(this.agContextMenuService.createTaskClicked.subscribe( async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openCreateTaskModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));
        this.agentStatesContextMenuSubscribed = true;

        this.subscriptions.push(this.agContextMenuService.giveAwardClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }

            this.openAwardModal(params.moduleKey, params.groupData.groupID, params.groupData, this.config.isCxOne)
        }));

        this.subscriptions.push(this.agContextMenuService.suggestCoachingClicked.subscribe(async params => {
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openSuggestCoachingModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));
    }

    /** Opens the Agent State Detail modal from the Context Menu on Groups column */
    openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any ) {
        if (this.isAgentStateDetailModalOpen) return;
        this.isAgentStateDetailModalOpen = true;
        this.navigationService.showOverlay = true;

        this.modalRef = this.isCXOne
            ? this.ngbModal.open(AgentStateDetailCXOneComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: 'cx-one-ui' })
            : this.ngbModal.open(AgentStateDetailComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => {
            this.navigationService.showOverlay = false;
            this.isAgentStateDetailModalOpen = false;
        });

        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openCreateTaskModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TasksModalComponent,
            {size : "xl", centered: true, scrollable: true, modalDialogClass: null});
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: TasksModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    /** Opens the Thresholds modal from the Context Menu on module header */
    openThresholdsModal(moduleKey: string) {
        this.metrics = this.moduleManager.moduleSettings.selectedMetrics;
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(WidgetThresholdsModalComponent,
            {size : "med", centered: true, scrollable: true, modalDialogClass: this.config.isCxOne ? 'cx-one-ui' : null});
        this.modalRef.result.then(
            () => {
                this.metricThresholds = this.modalRef.componentInstance.metricThresholds;
                this.moduleManager.moduleOptions.onRefresh(false);
                this.moduleManager.requestPublishNeeded();
            },
            () => {}
        );
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: WidgetThresholdsModalComponent = this.modalRef.componentInstance;
        component.metrics = this.moduleManager.moduleSettings.selectedMetrics;
        component.moduleKey = moduleKey;
    }

    openAwardModal(moduleKey: string, groupID: number, selectedRow: any, _isCxone: boolean) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(ContextMenuAwardsDialogComponent,
            { size: "xl", centered: true, scrollable: true, modalDialogClass: 'give-award-dialog' });
        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
    }

    openSuggestCoachingModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(SuggestCoachingModalComponent,
            { size: "md", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: SuggestCoachingModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    /**
     * Calls the proper update function based on the gridType.
     *
     * @param moduleData
     * @param gridType
     */
    updateGridData(moduleData: IntervalModuleData, gridType: TableViewMode): void {
        switch (gridType) {
            case TableViewMode.MultipleMetrics:
                this.updateMultipleMetricsGrid(this.agGrid?.api, moduleData);
                break;
            case TableViewMode.SingleMetric:
                this.updateSingleMetricGrid(this.agGrid?.api, moduleData);
                break;
            case TableViewMode.Breakdown:
                this.updateBreakdownGrid(this.agGrid?.api, moduleData);
                break;
        }
    }

    /**
     * Destroy the scope containing the grid and remove the dom elements from div.intervalGridContainer
     */
    destroyGrid() {
        this.currentTableView = null;
        this.currentDataKey = undefined;
        this.totalsRowData = null;
        this.gridOptions = null;
    }

    /**
     * Updates the suffix after the module name.
     * The Single Metric view appends the name of the currently-selected metric to the module name.
     * Suffix is removed for Multiple and Breakdown views
     */
    updateModuleNameSuffix(moduleData: IntervalModuleData) {
        this.currentTableView = moduleData.ViewMode;
        if (this.currentTableView === TableViewMode.SingleMetric && moduleData?.Metrics?.[0]) {
            const isRenamed = moduleData.Metrics[0]?.MetricCustomName != null && moduleData.Metrics[0]?.MetricCustomName != "";
            const suffix = isRenamed ?
                " - " + moduleData.Metrics[0].MetricCustomName +"*" :
                " - " + moduleData.Metrics[0].Abbreviation;
            this.moduleManager.setModuleNameSuffix(suffix);
        } else {
            this.moduleManager.removeModuleNameSuffix();
        }
    }

    /**
     * Returns the base gridOptions common to all three TableViewModes.
     *
     * @param gridReadyCallback Function callback when the grid's onGridReady event is fired.
     */
    defaultGridOptions(gridReadyCallback?: (gridReadyEvent: GridReadyEvent) => void): GridOptions {
        return {
            context: { moduleManager: this.moduleManager },
            getMainMenuItems: params => this.agGridHelperService.hideResetColumnsMenuItem(params, this.moduleManager.hasWriteAccess()),
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
                menuTabs: this.agGridHelperService.getMenuTabItems(this.moduleManager.hasWriteAccess()),
            },
            onGridReady: event => {
                if (!this.moduleManager.hasWriteAccess() && !this.isCXOne) {
                    this.agGridHelperService.setGridReadOnly(this.gridOptions);
                }

                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

                if (gridReadyCallback) {
                    gridReadyCallback(event);
                }

                this.restoreColumnState(); //Needed to restore columns size saved on the tablestate.
                if (!this.gridState || this.currentDataKey !== this.gridState.stateKey) {
                    this.agGridHelperService.sizeColumnsSmart(event); //Needed to resize the grid when adding/removing metrics or doing any modifications that will reload the grid.
                    this.tableStateChanged();
                }

                // Colton - 7/10/2024: Note that this is set to run with 10ms delay because there is a 0ms setTimeout in the gridReadyCallback above.
                // This is unique to this widget because the gridReadyCallback is used to set the gridOptions, which is needed before the grid is ready.
                // This needs to be refactored later when all widgets start using a shared grid component
                setTimeout(() => this.stateRestoreInProgress = false, 10); // Wait for the next tick to let events finish before allowing state save
                // Re-apply the user's quick filter when the grid is (re)created so typed text in the search box
                // Matches the pattern used in expanded-summary-pm.component.ts onGridReady.
                setTimeout(() => this.applyFilter(), 0);
            },
            onColumnMoved: () => this.tableStateChanged(),
            onSortChanged: () => this.tableStateChanged(),
            onColumnVisible: () => this.tableStateChanged(),
            overlayNoRowsTemplate: this.config.isCxOne ?
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
            onFilterChanged: () => this.tableStateChanged(),
            onColumnPinned: () => this.tableStateChanged(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    this.tableStateChanged();
                }
            },
            onGridSizeChanged: (event) => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.tableStateChanged();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }
            },
            getContextMenuItems: params => this.getContextMenuItem(params),
            suppressContextMenu: true,
            suppressNoRowsOverlay: true,
            tooltipInteraction: true,
            localeText: this.localeText,
            components: {
                metricMenuRenderer: SimpleContextMenuRenderer,
            },

        };
    }

    async queueLaunchCoaching(params): Promise<void> {
        const baseUrl = this.navigationService.cxoneTokenUrls?.niceBaseUrl;
        const metricName = params.column.colDef.headerName;
        await this.moduleHelperService.launchCoachingClicked(baseUrl, params.node.data.GroupData.groupID, metricName);
    }

    getContextMenuItem(params): (string | MenuItemDef)[] {
        if (params.node && params.node.group && params.node.key && params.context.moduleManager.moduleSettings.ListByInterval) return []; //Breakdown totals shouldn't show context menu
        if (typeof params.value === 'string') {
            return this.agGridHelperService.getContextMenuItems(params);
        }
        if (this.config.isCxOne) {
            return [{
                name: 'Launch Coaching',
                action: () => this.queueLaunchCoaching(params),
                disabled: false,
                icon: `<svg class="icon-16 fill-actionCellIcon">
                            <use href="../App_Themes/CXone/icons/widgets/launch-coaching.svg#launch-coaching"></use>
                       </svg>` ,
            }];
        } else {
            return [{
                name: this.cvLocalizationService.getCachedText("Objectives", "Objectives_Details", "Objectives Details"),
                action: () => this.objectivesDetailsModalClicked(params),
                icon: `<svg class="icon-16" style="fill: #6a7480;">
                            <use href="../App_Themes/clearview-default/icons/dashboard/gauge-range.svg#gauge-range"></use>
                       </svg>`,
            }];
        }
    }

    private objectivesDetailsModalClicked(params: any) {
        let value = angular.copy(params.value);

        const metricMatch = this.data.Metrics.find(m => m.MetricID === value.MetricID);
        if (metricMatch.NumericType === NumericType.Percent)
            value.MetricValue *= 100;

        this.objectivesDetailsService.openObjectivesDetailsModal(
            value,
            params.node.data.GroupData,
            this.data.ViewMode === TableViewMode.SingleMetric
                ? params.context.getMetric().MetricName
                : params.column.colDef.headerName,
        );
    }

    /**
     * Creates the gridOptions for multiple metrics
     */
    // Multiple Metrics View:
    // Date Header | Metric 1 Abv Header | Metric 2 Abv Header | ...
    // ------------+---------------------+---------------------+----
    // Date 1      | Metric 1 Value      | Metric 2 Value      | ...
    // Date 2      | Metric 1 Value      | Metric 2 Value      | ...
    createMultipleMetricsGrid(moduleData: IntervalModuleData) {
        if (!this.isFirstLoad && this.gridStateLoaded)
            this.gridState = null;

        const gridOptions = this.defaultGridOptions(gridReadyEvent => {
            this.updateMultipleMetricsGrid(gridReadyEvent.api, moduleData);
        });

        gridOptions.columnDefs = [
            {
                colId: "Interval",
                field: "Interval",
                headerName: this.cvLocalizationService.getCachedText("Interval_Module", "Date", "Date"),
                cellRenderer: (params) => params?.value,
                // pinnedRowCellRenderer: () => this.cvLocalizationService.getCachedText("Interval_Module", "Totals", "Totals"),
                cellRendererSelector: params => {
                    // TODO: Ag Grid Update 30.0.0 - pinnedRowCellRenderer is deprecated. 'Use cellRendererSelector instead'. This implementation may not work though
                    if (params.node.rowPinned) {
                        return {
                            component: () => this.isCXOne ? this.cxoneTotalText : (!this.showTotal ? this.avgsText : this.totalsText),
                            params: {
                                suppressCount: true,
                            },
                        };
                    }
                },
                comparator: (valueA, valueB, nodeA, nodeB) => {
                    const dataA = nodeA?.data as TotalItem;
                    const dataB = nodeB?.data as TotalItem;

                    if (!dataA && !dataB) {
                        return 0;
                    } else if (!dataA && dataB) {
                        return -1;
                    } else if (dataA && !dataB) {
                        return 1;
                    } else {
                        return (new Date(dataA.IntervalValue)).getTime() - (new Date(dataB.IntervalValue)).getTime();
                    }
                },
                suppressMenu: this.isSharedURL,
            }
        ];

        // For gridOptions.context.getMetric()
        const metricIDToMetric = new Map<string, Metric>();

        moduleData?.Metrics?.forEach(metric => {
            const metricID = metric.MetricID;
            this.setHasThreshold(moduleData.IntervalTotals, metricID);

            const isRenamed = metric.MetricCustomName && metric.MetricCustomName !== "";
            const valueGetter = (node: IRowNode) => (node?.data as TotalItem)?.AllMetricData[metricID];
            gridOptions.columnDefs.push({
                colId: metricID.toString(),
                valueGetter: params => valueGetter(params.node),
                headerName: isRenamed ? metric.MetricCustomName+"*" : metric.Abbreviation,
                headerTooltip: isRenamed ?
                    this.cvLocalizationService.getCachedText(
                        "Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + metric.Abbreviation : null,
                type: "metricColumn",
                filter: 'agTextColumnFilter',
                filterParams: {
                    getValue: (node: RowNode) => valueGetter(node)?.MetricValue,
                },
                suppressMenu: this.isSharedURL,
            });
            metricIDToMetric.set(metricID.toString(), metric);
        });

        gridOptions.context.getMetric = params => metricIDToMetric.get(params.colDef.colId);
        gridOptions.getRowId = ({data: rowData}) => rowData?.Interval;

        return this.agGridHelperService.createGridOptions(gridOptions, {
            enableCopy: true,
        });
    }

    setHasThreshold(rowItems: {[interval: string]: TotalItem} | SingleMetricItem[] | BreakdownItem[],
        metricID: number, interval?: string, isSingleMetric = false) {
        const threshold = this.getThreshold(metricID);
        const hasThreshold = threshold?.find(part => part.enabled)?.enabled ?? false;
        Object.values(rowItems).forEach(row => { // find where in the data can i store hasThreshold where it will be available in ag grid service base
            // table has 3 columns but one is hidden in case of one metric selected so metric column will be last index which is 1 even though there are 3 coldefs
            let md;
            if (!isSingleMetric) {
                md = row.AllMetricData[metricID];
            }
            else {
                md = row.AllMetricData[interval];
            }
            if (md) {
                md.hasThreshold = hasThreshold;
            }
        });
    }

    getThreshold(metricID: number) {
        if(this.metricThresholds == null) return null;
        return this.metricThresholds[metricID];
    }

    getDirection(metricID: number) {
        if (!this.metrics) return null;
        return this.metrics.find(m => m.Id === metricID)?.Direction;
    }

    getMetricType(metricID: number) {
        if (!this.metrics) return null;
        return this.metrics.find(m => m.Id === metricID)?.ConversionType;
    }

    isValueInThreshold(params: any, index: number) {
        if (params === null || params.data === null || params.data?.ID === 'total') return false;
        const threshold = this.getThreshold(params.value?.MetricID);
        const metricType = this.getMetricType(params.value?.MetricID);

        if (threshold == null) return false;
        if (threshold[index].enabled === false) return false;

        let metricValue = params.value?.MetricValue;
        if (!metricValue && metricValue !== 0) {
            return false;
        }

        let leftValue = threshold[index].left ?? 0;
        let rightValue = threshold[index].right ?? 0;

        switch(metricType) {
            case 3: // Time
                if((leftValue +'').includes(':') || (rightValue + '').includes(':')) {
                    leftValue = this.getSeconds(leftValue + '');
                    rightValue = this.getSeconds(rightValue + '');
                    metricValue = Math.floor(metricValue);
                } else {
                    metricValue = (metricValue).toFixed(2);
                }

                break;

            case 5: // Percent

                metricValue = (metricValue * 100).toFixed(2);
                metricValue = Number(metricValue);

                break;

            default: // All Others
                metricValue = (metricValue).toFixed(2);
        }

        switch (index) {
            case 0:
                return (metricValue >= leftValue);

            case 1:
                return (metricValue >= leftValue && metricValue <= rightValue);

            case 2:
                return (metricValue <= rightValue);

            default:
                return false;
        }
    }

    /** converts a time string to seconds */
    getSeconds(value: string) {
        let seconds = 0;
        const segments = value.split(':');
        for(let i = 0; i <= segments.length; i++) {
            const parsed = parseInt(segments[i], 10);
            if (Number.isNaN(parsed))
                seconds += 0;
            else
                seconds += parsed * Math.pow(60, segments.length - (i + 1));
        }
        return seconds;
    }

    /**
     * Creates the gridOptions for one metric
     */
    // Single Metric View:
    // First column is Group with Group values
    // Next columns are the interval segments (e.g., each interval date returned).
    //
    //  Group Header | Date 1 Header | Date 2 Header | ...
    //  -------------+---------------+---------------+----
    //  Group 1 Name | Value 1-1     | Value 1-2     | ...
    //  Group 2 Name | Value 2-1     | Value 2-2     | ...
    //
    createSingleMetricGrid(moduleData: IntervalModuleData) {
        if (!this.isFirstLoad && this.gridStateLoaded)
            this.gridState = null;

        let gridOptions = this.defaultGridOptions((gridReadyEvent: GridReadyEvent) => {
            this.updateSingleMetricGrid(gridReadyEvent.api, moduleData);
        });

        gridOptions.components = {
            ...this.defaultGridOptions().components,
            groupRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
            groupHeaderComponent: this.agGridHelperService.GroupHeaderComp,
        };

        gridOptions.columnDefs = [
            {
                field: "GroupData",
                colId: "Group",
                type: "groupColumn",
                pinned: "left",
                headerComponent: 'groupHeaderComponent',
                cellRenderer: 'groupRenderer',
                //pinnedRowCellRenderer: () => this.cvLocalizationService.getCachedText("Interval_Module", "Totals", "Totals"),
                cellRendererSelector: params => {
                    // TODO: Ag Grid Update 30.0.0 - pinnedRowCellRenderer is deprecated. 'Use cellRendererSelector instead'. This implementation may not work though
                    if (params.node.rowPinned) {
                        return {
                            component: () => this.isCXOne ? this.cxoneTotalText : (!this.showTotal ? this.avgsText : this.totalsText),
                            params: {
                                suppressCount: true,
                            },
                        };
                    }
                },
                filterParams: {
                    getValue: (node: RowNode) => (node?.data?.GroupData as ModuleDataGroupItem)?.groupName,
                },
                suppressMenu: this.isSharedURL,
            },
            {
                field: "ParentGroupData",
                headerName: this.cvLocalizationService.getCachedText('Interval_Module', 'Parent_Label', "Parent"), // Check Localization for Parent
                type: "groupColumn",
                // pinnedRowCellRenderer: () => "",
                cellRendererSelector: params => {
                    // TODO: Ag Grid Update 30.0.0 - pinnedRowCellRenderer is deprecated. 'Use cellRendererSelector instead'. This implementation may not work though
                    if (params.node.rowPinned) {
                        return {
                            component: () => "",
                            params: {
                                suppressCount: true,
                            },
                        };
                    }
                },
                hide: !this.parentVisible,
                valueFormatter: (params: ValueFormatterParams) => params?.node?.data?.ParentGroupData?.groupName ??
                    this.cvLocalizationService.getCachedText('Interval_Module', 'No_Parent', 'No Parent'),
                filterParams: {
                    getValue: (node: RowNode) => (node?.data?.ParentGroupData as ModuleDataGroupItem)?.groupName,
                },
                suppressMenu: this.isSharedURL,
            }
        ];

        moduleData?.IntervalOrder?.forEach(intervalStr => {
            this.setHasThreshold(moduleData.SingleMetricItems, moduleData.Metrics[0].MetricID, intervalStr, true);
            const valueGetter = (node: IRowNode) => (node?.data as SingleMetricItem)?.AllMetricData[intervalStr];
            gridOptions.columnDefs.push({
                colId: intervalStr,
                headerName: intervalStr,
                type: "metricColumn",
                cellRenderer: !this.config.isCxOne ? 'metricMenuRenderer' : '',
                cellRendererParams: {isCXOne: this.config.isCxOne},
                valueGetter: params => valueGetter(params.node),
                filter: 'agTextColumnFilter',
                filterParams: {
                    getValue: (node: RowNode) => valueGetter(node)?.MetricValue,
                },
                suppressMenu: this.isSharedURL,               
            });
        });

        gridOptions.context.getMetric = () => moduleData.Metrics[0];
        gridOptions.context.getGroupData = params => params.node.data.GroupData;

        gridOptions.getRowId = ({ data }) => data.GroupData.groupID;

        gridOptions = this.agGridHelperService.createGridOptions(gridOptions, {
            enableCopy: true
        });
        gridOptions.defaultColDef = gridOptions.defaultColDef ?? {};
        // Dates are their own columns ascending from left to right
        gridOptions.defaultColDef.suppressMovable = true;

        return gridOptions;
    }

    /**
     * Creates the gridOptions for the breakdown view.
     * Breakdown can apply to one or more metrics.
     * Breakdown is toggled in the Date Range section of the settings.
     */
    // Breakdown View:
    // Date Header     | Metric 1 Abv Header     | Metric 2 Abv Header     | ...
    // ----------------+-------------------------+-------------------------+----
    // > Interval 1    | Metric-Interval Total 1 | Metric-Interval Total 2 | ...
    // v Interval 2    | Metric-Interval Total 1 | Metric-Interval Total 2 | ...
    //    Group 1 Name | Group-Metric Value 1    | Group-Metric Value 2    | ...
    //    Group 2 Name | Group-Metric Value 1    | Group-Metric Value 2    | ...
    // > Interval 3    | Metric-Interval Total 1 | Metric-Interval Total 2 | ...
    createBreakdownIntervalsGrid(moduleData: IntervalModuleData): GridOptions {
        if (!this.isFirstLoad && this.gridStateLoaded)
            this.gridState = null;

        const gridOptions = this.defaultGridOptions((gridReadyEvent: GridReadyEvent) => {
            this.updateBreakdownGrid(gridReadyEvent.api, moduleData);
        });

        gridOptions.components = {
            ...this.defaultGridOptions().components,
            groupRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
            groupHeaderComponent: this.agGridHelperService.GroupHeaderComp,
        };

        gridOptions.columnDefs = [
            {
                // This is the column value that will be grouped on.
                field: "Interval",
                colId: "Interval",
                rowGroup: true,
                hide: true, // Hide the actual column to avoid repeating the interval for each row
                cellRenderer: 'agGroupCellRenderer',
                cellRendererParams: {
                    suppressCount: true,
                },
                suppressMenu: this.isSharedURL,
            },
            {
                field: "GroupData",
                colId: "Group",
                type: "groupColumn",
                headerComponent: "groupHeaderComponent",
                cellRenderer: 'agGroupCellRenderer',
                pinned: "left",
                // pinnedRowCellRenderer: () => this.cvLocalizationService.getCachedText("Interval_Module", "Totals", "Totals"),
                cellRendererSelector: params => {
                    // TODO: Ag Grid Update 30.0.0 - pinnedRowCellRenderer is deprecated. 'Use cellRendererSelector instead'. This implementation may not work though
                    if (params.node.rowPinned) {
                        return {
                            component: () => this.isCXOne ? this.cxoneTotalText : (!this.showTotal ? this.avgsText : this.totalsText),
                            params: {
                                suppressCount: true,
                            },
                        };
                    }
                },
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: 'groupRenderer',
                },
                aggFunc: 'intervalAgg',
                filterParams: {
                    getValue: (node: RowNode) => (node?.data?.GroupData as ModuleDataGroupItem)?.groupName,
                },
                suppressMenu: this.isSharedURL,
            }
        ];

        // For gridOptions.context.getMetric()
        const metricIDToMetric = new Map<string, Metric>();

        moduleData.Metrics?.forEach(metric => {
            const metricID = metric.MetricID;
            this.setHasThreshold(moduleData.BreakdownItems, metricID);
            const valueGetter = (node: IRowNode) => (node?.data as BreakdownItem)?.AllMetricData[metricID];
            const isRenamed = metric.MetricCustomName && metric.MetricCustomName !== "";
            gridOptions.columnDefs.push({
                colId: metricID.toString(),
                valueGetter: params => valueGetter(params.node),
                headerName: isRenamed ? metric.MetricCustomName+"*" : metric.Abbreviation,
                headerTooltip: isRenamed ?
                    this.cvLocalizationService.getCachedText(
                        "Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + metric.Abbreviation : null,
                type: "metricColumn",
                cellRenderer: 'metricMenuRenderer',
                cellRendererParams: {isCXOne: this.config.isCxOne,
                    groupLevel: this.data.BreakdownItems[0]?.GroupData.groupLevel,
                    groupType: this.data.BreakdownItems[0]?.GroupData.groupType,
                },
                aggFunc: 'breakdownAgg',
                filterParams: {
                    getValue: (node: RowNode) => valueGetter(node)?.MetricValue,
                },
                suppressMenu: this.isSharedURL,
            });
            metricIDToMetric.set(metricID.toString(), metric);
        });

        gridOptions.context.getMetric = params => metricIDToMetric.get(params.colDef.colId);
        gridOptions.getRowId = ({data: data}) => `${data.Interval}_${data.GroupData.groupID}`;
        gridOptions.groupDefaultExpanded = -1;
        gridOptions.suppressAggFuncInHeader = true; // If the metric is called "Calls", AggFuncInHeader displays it as "func(Call)". Suppress this function string.
        //gridOptions.groupSuppressAutoColumn = true; // Don't display the column for "Interval" // AG Grid 30.0.0 Replaced with groupDisplayType = 'custom'
        gridOptions.groupDisplayType = 'custom';
        gridOptions.autoGroupColumnDef = {
            cellRendererParams: {
                suppressCount: true, // This suppresses the row group header's count of the number of rows in the group.
            }
        };
        /* CXCV-1090 - Colton: This is needed to allow groups to sort by default when there is no sorting in place. Prior to this,
            the new intervals would get appended to the bottom. So if the existing grouping was by hour (12:00 -> 1:00 -> 2:00) and
            it was switched to half hour, it would be 12:00 -> 1:00....11:00 -> 12:30. This comparator reorganizes the groups so the sub-hour intervals
            are put in place where they belong: 12:00 -> 12:30 -> 1:00.
        */
        gridOptions.initialGroupOrderComparator = ({nodeA, nodeB}): number => {
            if (nodeA && nodeB) {
                try {
                    const nodeAInterval = Date.parse(nodeA.key);
                    const nodeBInterval = Date.parse(nodeB.key);
                    return nodeAInterval.compareTo(nodeBInterval);
                } catch {
                    return 0;
                }
            }
            else return 0;
        };

        return this.agGridHelperService.createGridOptions(gridOptions, {
            enableCopy: true
        });
    }

    /**
     * Toggles between showing and hiding the totals row
     * Callback on the menu item
     */
    toggleTotalsRow() {
        if(this.isCXOne)
            this.showTotalsRow = !this.showTotalsRow;
        this.setPinnedRowData();
        this.queueStateSave();
    }

    getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Interval_Module", key, defaultValue);
    }

    updateColumnDefs(isBreakdown: boolean = false)
    {
        const newGridOptions = isBreakdown ?
            (this.data.ListByInterval ?
                this.createBreakdownIntervalsGrid(this.data) :
                this.createBreakdownGroupsGrid(this.data)
            ) :
            this.createMultipleMetricsGrid(this.data);

        const columnDefs = newGridOptions.columnDefs;

        let numMatchedDefs = 0;
        let newColumnFound = false;
        const newColumnDefs = [];
        columnDefs.forEach(agColDef => {
            const colDef = agColDef as ColDef;
            newColumnDefs.push(agColDef);
            //check if this column was in our column defs before
            for (let i = 0; i < this.gridOptions.columnDefs.length; i++) {
                if (this.gridOptions.columnDefs[i].colId === colDef.colId) {
                    numMatchedDefs++;
                    return;
                }
            }
            newColumnFound = true;
        });

        let columnOrderChange = 0;
        if (newColumnDefs.length == this.gridOptions.columnDefs.length) {
            for (let j = 2; j < this.gridOptions.columnDefs.length; j++) {
                if (newColumnDefs[j].headerName != this.gridOptions.columnDefs[j].headerName) {
                    columnOrderChange++;
                }
            }
        }

        let newMetricAdded = false;
        if (this.metrics?.toString() !== this.moduleManager.moduleSettings.selectedMetrics.toString()) {
            this.metrics = this.moduleManager.moduleSettings.selectedMetrics;
            newMetricAdded = true;
        }

        if (newColumnFound || numMatchedDefs < this.gridOptions.columnDefs.length || columnOrderChange > 0 || newMetricAdded) {
            this.gridOptions.columnDefs = columnDefs;
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('columnDefs', []);
                this.agGrid?.api.setGridOption('columnDefs', columnDefs);
                if (this.gridState) {
                    this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api);
                    this.tableStateChanged();
                }
            }
            return true;
        }
        return false;
    }

    /**
     * Sets the module data for the Multiple Metrics view.
     *
     * @param api
     * @param moduleData
     */
    updateMultipleMetricsGrid(api: GridApi, moduleData: IntervalModuleData) {
        const rowData: TotalItem[] = moduleData?.IntervalTotals != null ? Object.values(moduleData?.IntervalTotals) : [];
        rowData?.sort((a, b) => moduleData?.IntervalOrder.indexOf(a.Interval) - moduleData?.IntervalOrder.indexOf(b.Interval));
        moduleData.Metrics?.forEach(metric => {
            const metricID = metric.MetricID;
            this.setHasThreshold(moduleData.IntervalTotals, metricID);
        });
        this.totalsRowData = moduleData?.Totals;
        this.avgsRowData = {};
;
        if (api) {           
            api.setGridOption('rowData', [...rowData]);
        }
        this.setPinnedRowData(api);
        this.updateColumnDefs();
        // Re-apply the quick filter after data update so search text persists across auto-refreshes and date range changes.
        this.applyFilter();
    }

    /**
     * Sets the module data for the Single Metric view.
     * Data for the totals row is transformed.
     *
     * @param api
     * @param moduleData
     */
    updateSingleMetricGrid(api: GridApi, moduleData: IntervalModuleData) {
        if (api && moduleData?.SingleMetricItems) {
            moduleData.IntervalOrder.forEach(interval => {
                const metricID = moduleData.Metrics[0].MetricID;
                this.setHasThreshold(moduleData.SingleMetricItems, metricID, interval, true);
            });
            // In case the configured metric changed:
            this.gridOptions.context.getMetric = () => moduleData.Metrics[0];

            // ensures the group header component updates the name on drill up or drill down
            api.setGridOption('columnDefs', api.getColumnDefs());

            // The data for the totals row needs to be transformed here, since the data is expected to be grouped by Interval, not MetricID (as Totals is now)
            // Also remove the objective data, otherwise the colors will be applied to it.
            const totalsRowData: { [interval: string]: MetricData } = {};
            if (moduleData?.IntervalTotals) {
                Object.values(moduleData.IntervalTotals).forEach(totalItem => {
                    const metricData = Object.values(totalItem.AllMetricData)[0];
                    if (metricData)
                        metricData.HasPO = false;
                    totalsRowData[totalItem.Interval] = metricData;
                });
            }
            this.totalsRowData = totalsRowData;

            const avgsRowData: { [interval: string]: MetricData } = {};
            if (moduleData?.AverageTotals) {
                Object.values(moduleData.AverageTotals).forEach(avgItem => {
                    const metricData = Object.values(avgItem.AllMetricData)[0];
                    if (metricData)
                        metricData.HasPO = false;
                    avgsRowData[avgItem.Interval] = metricData;
                });
            }
            this.avgsRowData = {};

            api.setGridOption('rowData', [...moduleData.SingleMetricItems]);
            api.redrawRows();// ensures that the obtained data is displayed correctly
            this.setPinnedRowData(api);
            // Re-apply the quick filter after data update so search text persists across auto-refreshes and date range changes.
            this.applyFilter();
        }
    }

    /**
     * Sets the module data for the Breakdown view.
     * The metricData objects are augmented to add the interval total value.
     * Group data is augmented to add the interval string.
     * This augmentation is for the aggFunc values, which are displayed in the row group headers. Metric data must be aggregated on the server.
     *
     * @param api
     * @param moduleData
     */
    updateBreakdownGrid(api: GridApi, moduleData: IntervalModuleData) {
        if (api && moduleData?.BreakdownItems) {
            moduleData.Metrics.forEach(metric => {
                const metricID = metric.MetricID;
                this.setHasThreshold(moduleData.BreakdownItems, metricID);
            });
            moduleData.BreakdownItems.forEach(breakdownItem => {
                let metricID;
                let metricData;
                for ([metricID, metricData] of Object.entries(breakdownItem.AllMetricData)) {
                    metricData = metricData || {} as MetricData;
                    metricData["intervalTotal"] = moduleData.IntervalTotals[breakdownItem.Interval].AllMetricData[metricID];
                    if (!moduleData.ListByInterval)
                        metricData["groupTotal"] = moduleData.GroupTotals[breakdownItem.GroupData.groupID]?.AllMetricData[metricID];
                }
                const settings = this.moduleManager.getModuleSettings();
                const singleDay = settings.selectedDateRange?.numberOfDays === 1;
                if (singleDay && !this.isCXOne) { //cxone will always be a single day because their widget doesnt allow for more days
                    const fullTime = breakdownItem.Interval;
                    breakdownItem.GroupData["interval"] = fullTime.slice(fullTime.indexOf(" ") + 1, fullTime.length); // sub header interval only time
                } else {
                    breakdownItem.GroupData["interval"] = breakdownItem.Interval; // date and time
                }
            });

            this.totalsRowData = moduleData.Totals;
            this.avgsRowData = moduleData.Averages;
            api.setGridOption('rowData', [...moduleData.BreakdownItems]);
            // setRowData was reverting groupLevel and groupType in the column def so resetting it here below
            this.gridOptions.columnDefs.forEach(col => {
                if (col.type === "metricColumn") {
                    col.cellRendererParams = {isCXOne: this.config.isCxOne,
                        groupLevel: this.data.BreakdownItems[0]?.GroupData.groupLevel,
                        groupType: this.data.BreakdownItems[0]?.GroupData.groupType
                    };
                }
            });
            api.setGridOption('columnDefs', this.gridOptions.columnDefs); // ensures grouplevel and grouptype gets updated in cell rendererParams
            // ensures the group header component updates the name on drill up or drill down
            api.setColumnDefs(api.getColumnDefs());
            api.redrawRows();
            this.setPinnedRowData(api);
            this.updateColumnDefs(true);
            this.restoreColumnState(); //Needed to restore columns size and order saved on the tablestate.
            // Re-apply the quick filter after data update so search text persists across auto-refreshes and date range changes.
            this.applyFilter();
        }
    }

    /**
     * If the user has enabled the totals or avg row, this sets the data.
     * Otherwise it clears the data to hide the row (for CXOne). 
     * For PM either avg or total row should always be shown
     *
     * @param api Optional GridApi reference.
     */
    setPinnedRowData(api?: GridApi) {
        api = api || this.agGrid?.api;
        if (!api) {
            return;
        }
        if (((this.isCXOne && this.showTotalsRow) || (!this.isCXOne && this.showTotal)) && this.totalsRowData) {
            api.setGridOption('pinnedBottomRowData', [{
                AllMetricData: this.totalsRowData,
                GroupData: { groupName: this.cvLocalizationService.getCachedText("Interval_Module", "Totals", "Totals") },
                Interval: ""
            }]);
        }
        //will be implemented in the future
        //else if (!this.isCXOne && this.hideTotal && this.avgsRowData) {
        //    api.setGridOption('pinnedBottomRowData', [{
        //        AllMetricData: this.avgsRowData,
        //        GroupData: { groupName: this.cvLocalizationService.getCachedText("Interval_Module", "Averages", "Averages") },
        //        Interval: this.cvLocalizationService.getCachedText("Interval_Module", "Averages", "Averages")
        //    }]);
        //}
        else {
            api.setGridOption('pinnedBottomRowData', []);
        }
    }

    /**
     * Saves the state.  If `state` is not specified, all states are saved after a 500ms debounce.
     *
     * @param state Optional specific state to save
     */
    queueStateSave(state?: IntervalState) {
        if (this.stateRestoreInProgress)
            return;

        // If a general state save is already scheduled when a specific state save is requested, it'll instead continue to debounce the general state save.
        if (!this.stateSaveTimeout && state != null) {
            this.moduleManager.saveState(state);
        } else {
            if (this.stateSaveTimeout) {
                clearTimeout(this.stateSaveTimeout);
            }
            this.stateSaveTimeout = setTimeout(async () => {
                this.stateSaveTimeout = null;
                if (await this.moduleManager.saveState()) {
                    this.moduleManager.requestPublishNeeded();
                }
            }, 500);
        }
    }

    /**
     * Update the state object and queue a state save
     * Grid callback for events onColumnMoved and onSortChanged
     */
    tableStateChanged() {
        this.gridState = {
            stateKey: this.currentDataKey,
            state: this.agGridHelperService?.getGridState(this.agGrid.api),
        };
        this.queueStateSave();
    }

    /**
     * This function restores the grid column state, if the previous state's key matches the state key.
     */
    restoreColumnState() {
        if (!this.gridState || !this.gridState.stateKey || !this.gridState.state) {
            return;
        }
        if (this.currentTableView != null && this.currentDataKey === this.gridState.stateKey) {
            this.agGridHelperService.loadGridState(this.agGrid.api, this.gridState.state);
        }
    }

    /**
     * Restore whether the totals row should be displayed.
     *
     * @param state boolean indicating show or hide the totalsRow.  true = show, false = hide.
     */
    restoreTotalsRowState(state: boolean) {
        this.isCXOne ? this.showTotalsRow = state : this.showTotal = state;
        this.setPinnedRowData();
    }

    /** Toggle visibility of Location or Column (Parent for cxone) */
    toggleparentVisible() {
        this.parentVisible = !this.parentVisible;
        this.queueStateSave(IntervalState.ParentVisible);
        this.agGrid?.api.moveColumn('ParentGroupData', 1);
        this.agGrid?.api.setColumnVisible('ParentGroupData', this.parentVisible);
    };

    ///////////////////////////////////////////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////////////// Chart methods ////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * When the DOM is set up and the chart hasnt already been set up, grab the canvas element and set up the grid.
     */
    ngAfterViewChecked(): void {
        if (this.moduleDisplay !== IntervalModuleDisplay.Table && this.chartObject == null) {
            this.createChart();
        }
    }

    /**
     * Adds the table-specific menu items to the module menu
     */
    addMenuItemsTable() {
        this.moduleManager.addMenuItem({
            name: "Table Display", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
            icon: "tableDisplay",
            getEnabled: () => this.gridOptions && this.agGrid?.api && this.gridOptions.columnDefs.length > 0 && !this.isNoData,
            type: this.isCXOne ? ModuleManagerMenuItemType.CheckMenu : ModuleManagerMenuItemType.RadioCheckMenu,
            getCheckItems: () => {
                if (this.data?.ViewMode === TableViewMode.SingleMetric) {
                    return this.isCXOne ? this.displayCheckItems : this.tableDisplayItems;
                }
                return this.isCXOne ? this.displayCheckItems.slice(0, 1) : this.tableDisplayItems.slice(0, 2);
            },
            getItemName: i => this.isCXOne ? this.displayCheckItems[i].displayName : this.tableDisplayItems[i].displayName,
            getItemInputType: i => { return i <= this.tableDisplayItems.length - 1 ? this.tableDisplayItems[i]?.inputType : 'checkbox' }, //not checking for cxone here since they wont be using the radio buttons
            isItemChecked: i => this.isCXOne ? this.displayCheckItems[i].checked : this.tableDisplayItems[i].checked,
            onCheckClicked: (i) => {
                if (this.isCXOne) {
                    this.displayCheckItems[i].checked = !this.displayCheckItems[i].checked;
                    switch (i) {
                        case 0: // Show Totals
                            this.toggleTotalsRow();
                            break;
                        case 1:
                            this.toggleparentVisible();
                            break;
                    }
                }
                else {
                    if (this.tableDisplayItems[i].inputType === 'radio') {
                        this.showTotal = this.tableDisplayItems[i].value;
                        this.tableDisplayItems.filter(i => i.inputType === 'radio').forEach((item) => { item.checked = this.showTotal });
                    }
                    else {
                        this.tableDisplayItems[i].checked = !this.tableDisplayItems[i].checked;
                    }

                    switch (i) {
                        case 0: // Show Totals
                        case 1:
                            this.toggleTotalsRow();
                            break;
                        case 3: //case 2 is the divider
                            this.toggleparentVisible();
                            break;
                    }
                }
            },
            readOnlyEnabled: this.isCXOne
        } as IModuleManagerMenuItem);

        if(this.moduleManager.hasWriteAccess()) {
            this.moduleManager.addMenuItem({
                name: "Columns", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
                icon: "columns",
                getEnabled: () => this.gridOptions && this.agGrid?.api && this.gridOptions.columnDefs.length > 0 && !this.isNoData,
                type: ModuleManagerMenuItemType.CheckMenu,
                getCheckItems: () => this.agGrid?.api?.['columnModel']?.gridColumns,
                getItemName: (i) => {
                    const column = this.agGrid?.api['columnModel'].gridColumns[i];
                    if (column) {// During column changes the state can have columns that are no longer in the list
                        const colDef = column.getColDef();
                        // GroupData and Interval doesn't have headerName Property but field.
                        return colDef.headerName ? colDef.headerName : column.getColDef().field;
                    }
                    else return '';
                },
                isItemChecked: i => this.agGrid?.api['columnModel'].gridColumns[i].isVisible(),
                onCheckClicked: i => this.toggleColumnVisible(i),
            } as IModuleManagerMenuItem);
        }
    }
    /**
     * Adds the chart-specific menu items to the module menu
     */
    addMenuItemsChart() {
        if (!this._localizationsLoaded) {
            return;
        }

        if (!this.config.isCxOne) {
            this.moduleManager.addMenuItem({
                name: 'Themes',
                icon: 'palette',
                type: ModuleManagerMenuItemType.SubMenu,
                subItems: this.themeMenuOptions,
                getEnabled: () => this.chartObject != null,
            } as ModuleManagerSubMenu);
        }

        this.moduleManager.addMenuItem({
            name: "Chart Type",
            icon: "chartType",
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Line", "Line"),
                icon: "lineChart",
                click: () => this.setChartType("line"),
            },
            {
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Vertical_Bar", "Vertical Bar"),
                icon: "barChart",
                click: () => this.setChartType("bar"),
            },
            {
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Horizontal_Bar", "Horizontal Bar"),
                icon: 'horizontalBarChart',
                click: () => this.setChartType('horizontalBar')
            }]
        } as ModuleManagerSubMenu);

        this.moduleManager.addMenuItem({
            name: "Legend_Position",
            icon: "legend",
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Top", "Top"),
                icon: "positionTop",
                click: () => this.setLegendPosition("top", true),
            },
            {
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Bottom", "Bottom"),
                icon: "positionBottom",
                click: () => this.setLegendPosition("bottom", true),
            },
            {
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Left", "Left"),
                icon: "positionLeft",
                click: () => this.setLegendPosition("left", true),
            },
            {
                name: this.cvLocalizationService.getCachedText("Interval_Module", "Right", "Right"),
                icon: "positionLeft",
                click: () => this.setLegendPosition("right", true),
            }, 
            {
                type: 'divider'
            },
            {
                name:  !this.showLegendPosition ? this.getLocaleText('Show_Legend_Chart','Show Legends') : this.getLocaleText('Hide_Legend_Chart','Hide Legends'),
                icon: "eye",
                click: () => { 
                    this.hideShowLegendClicked = true;
                    this.setLegendPosition('hide', true);
                },
            }]
        } as ModuleManagerSubMenu);

    };

    setChartType(type) {
        // If same type, no need to rerender.
        if (this.chartType === type) {
            return;
        }

        this.zoom.cur = 1;
        // Set type string, update the chart.
        // Changing chart type requires a full chart recreate.
        this.chartType = type;
        //reset chart scroll if chart type changes
        this.chartObject.chart.canvas.parentElement.style['height'] = `max(100%, 100%)`;
        this.chartObject.chart.canvas.parentElement.style['width'] = `max(100%, 100%)`;
        this.createChart();

        //Update state
        this.chartState.chartType = type;
        this.moduleManager.saveState('chartState');
        this.moduleManager.requestPublishNeeded();
    }

    /**
     * Sets the chart padding based on the legend position.
     * This method is used during state restoration and by setLegendPosition.
     */
    applyLegendPadding(position: string): void {
        switch (position) {
            case 'top':
                this.chartPadding = { top: 0, bottom: 0, left: 0, right: 0 };
                break;
            case 'bottom':
                this.chartPadding = { top: 16, bottom: 0, left: 0, right: 0 };
                break;
            case 'left':
            case 'right':
                this.chartPadding = { top: 16, bottom: 8, left: 0, right: 0 };
                break;
            default:
                this.chartPadding = { top: 0, bottom: 0, left: 0, right: 0 };
                break;
        }
    }

    async setLegendPosition(position, recreateChart) {
        // Set position string
        if (recreateChart) {
            if (position === 'hide') {
                this.showLegendPosition = !this.showLegendPosition;
                this.chartState.showLegendPosition = this.showLegendPosition;
                position = this.chartState.legendPosition;
            } else {
                this.showLegendPosition = true;
                this.chartState.showLegendPosition = true;
            }
        }
        //Set position string
        this.chartLegendPosition = position;
        this.chartState.legendPosition = position;

        //Set padding values
        if (position === 'hide') {
            this.updateChartLegend();
        } else {
            this.applyLegendPadding(position);
        }

        // Apply changes
        // Moving the legend requires the chart to be completely recreated instead of just updated.
        // This method (using a variable rather than direct manipulation) also allows the padding values to be retained if the chart is recreated in any way.
        this.createChart();
        this.addMenuItemsChart();
        if (await this.moduleManager.saveState("chartState")) {
            this.moduleManager.requestPublishNeeded();
        }
    }

    legendClickHandler(e, item) {
        const index = item.datasetIndex;
        const meta = this.chartObject?.chart.getDatasetMeta(index) as any;
        meta.hidden = !this.legendItemsHidden.get(meta._dataset.metricID);

        this.legendItemsHidden.set(meta._dataset.metricID, meta.hidden);
        if (this.chartType === 'horizontalBar') {
            if (meta.hidden) {
                this.chartAxisShowStates[meta.xAxisID] -= 1;
                if (this.chartAxisShowStates[meta.xAxisID] === 0) {
                    this.chartObject.chart.options.scales[meta.xAxisID].display = false;
                }
            } else {
                this.chartAxisShowStates[meta.xAxisID] += 1;
                this.chartObject.chart.options.scales[meta.xAxisID].display = true;
            }
        }
        else {
            if (meta.hidden) {
                this.chartAxisShowStates[meta.yAxisID] -= 1;
                if (this.chartAxisShowStates[meta.yAxisID] === 0) {
                    this.chartObject.chart.options.scales[meta.yAxisID].display = false;
                }
            } else {
                this.chartAxisShowStates[meta.yAxisID] += 1;
                this.chartObject.chart.options.scales[meta.yAxisID].display = true;
            }
        }

        this.chartObject?.update();
    }

    createChart() {
        // Check for null/empty data sets
        // If found, create an empty chart.
        if (this.data == null || this.data.GraphItems == null || this.data.GraphItems?.length === 0) {
            this.chartConfig = {
                type: 'line',
                data: {
                    labels: ['0'],
                    datasets: [{
                        label: '',
                        backgroundColor: 'rgba(0,0,0,0)',
                        borderColor: 'rgba(0,0,0,0)',
                        data: [0]
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                        duration: this.config.isCxOne ? 0 : 1,
                    },
                    scaleShowGridLines: true,
                    scaleShowVerticalLines: true,
                    showTooltips: false,
                    scales: {
                        y: {
                            ticks: {
                                beginAtZero: true
                            }
                        }
                    }
                },
                plugins: [this.backgroundcolor]
            } as ChartConfiguration;
            return;
        }
        const preppedDatasets = [];
        const seriesTypes = [];
        let xAxisLabels = [];
        const preparedYAxes = {};
        const transparentColor = 'rgba(0,0,0,0)'; //For the Alpha=0 value. Draws the border (which allows the under-the-line fill) but sets the actual line to be transparent.
        //swap to y when horizontal bar chart is selected
        const xAxisId = this.chartType === 'horizontalBar' ? 'y' : 'x'; // All items go on the same X-Axis, so we need an identifier for this one X-Axis.
        let colorIndex = 0;

        //Create a chart series for each dataset.
        for (const element of this.data.GraphItems) {
            if (element.NumericType === 3 && !element.MetricDisplayValues[0].includes(":"))
                element.NumericType = NumericType.Number;

            if (!this.firstAnimateComplete) {
                this.legendItemsHidden.set(element.MetricID, false);
            }
            // Create Y-Axes
            const yAxisId = 'YAxisType' + element.NumericType;

            // Check if a Y-Axis for this NumericType already exists, creating a new one if not.
            if (seriesTypes.indexOf(element.NumericType) < 0) {
                this.chartAxisShowStates[yAxisId] = 1;
                seriesTypes.push(element.NumericType);

                //Check for Type 3 (Time) metrics. The scale requires special axis-tick formatting. If detected, push an axis with the right configuration. Othewise, use the default.
                if (element.NumericType === 3) {
                    Object.assign(preparedYAxes, {[yAxisId]:
                        {
                            id: yAxisId,
                            display: true, // reset in verifyYAxisDisplay()
                            position: this.determinePosition(seriesTypes.length), //4 total types (enums: 0,1,2,3). Put the third and fourth types' axes on the right.
                            title: {
                                display: true,
                                font: {
                                    weight: 'bold',
                                },
                                text: element.NumericTypeName
                            },
                            ticks: {
                                callback: function (value) {
                                    const hours = Math.floor(value / 3600);
                                    const minutes = Math.floor((value % 3600) / 60);
                                    const seconds = Math.floor(value % 60);
                                    if (element.MetricDisplayValues[0].includes(".")) {
                                       const milliseconds =  Math.round((value - Math.floor(value)) * 1000);
                                        return (
                                        hours.toString().padStart(2, '0') + ":" +
                                        minutes.toString().padStart(2, '0') + ":" +
                                        seconds.toString().padStart(2, '0') + "." +
                                        milliseconds.toString().padStart(3, '0')
                                        );
                                    } else {
                                          return hours.toString().padStart(2, '0') + ":" + minutes.toString().padStart(2, '0') + ":" + seconds.toString().padStart(2, '0');
                                    }
                                },
                                autoSkip: true,
                                maxTicksLimit: Math.round(this.zoom.baseTicks * this.zoom.cur),
                                minRotation: 0,
                                maxRotation: 0
                            }
                        }
                    });
                } else {
                    //Default axis config
                    Object.assign(preparedYAxes, {[yAxisId]:
                        {
                            id: yAxisId,
                            display: true, // reset in verifyYAxisDisplay()
                            position: this.determinePosition(seriesTypes.length), //4 total types (enums: 0,1,2,3). Put the third and fourth types' axes on the right
                            title: {
                                display: true,
                                font: {
                                    weight: 'bold',
                                },
                                text: element.NumericTypeName
                            },
                        }
                    });
                }
            } else {
                this.chartAxisShowStates[yAxisId] += 1;
            }

            //Ensure a theme exists
            if (this.currentTheme == null) {
                this.currentTheme = this.moduleThemesService.ModuleThemes[0].Colors;
            }
            if (this.config.isCxOne) {
                this.currentTheme = this.moduleThemesService.ModuleThemes[12].Colors;
            }

            let uncountedMetrics = 0;
            const hasGroup = this.data.GraphItems.some(c => c.MetricName === "Group") ? 1 : 0;
            this.tooltipLegendItems.clear();
            this.data.GraphItems.map((c,index) => {
                if ((c.MetricID > 0) || c.MetricName === "Group") {
                    const isRenamed = c.MetricCustomName && c.MetricCustomName !== "";
                    if(isRenamed)
                        this.tooltipLegendItems.set(
                            index - uncountedMetrics - hasGroup, this.cvLocalizationService.getCachedText(
                                "Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + c.MetricName);
                }
                else
                    uncountedMetrics++;
            });

            //Add the series to be displayed
            preppedDatasets.push(
                {
                    //x and y axisIDs need to be swapped for horizontal bar charts
                    xAxisID: this.chartType === 'horizontalBar' ? yAxisId : xAxisId,
                    yAxisID: this.chartType === 'horizontalBar' ? xAxisId : yAxisId,
                    label: element.MetricCustomName && element.MetricCustomName != "" ? element.MetricCustomName+"*" : element.MetricName,
                    backgroundColor: this.currentTheme[colorIndex],
                    borderColor: this.data.GraphItems.length > 5 ? this.currentTheme[colorIndex] : transparentColor,
                    data: element.MetricValues,
                    displayValues: element.MetricDisplayValues,
                    dataType: element.NumericType, //Based on framework type: ClearView.Quality.Metrics.Enums.NumericType
                    fill: this.data.GraphItems.length > 5 ? false : 'origin',
                    hitRadius: this.config.isCxOne ? 1 : 12,
                    hoverRadius: 6,
                    pointRadius: 2,
                    pointBorderColor: this.currentTheme[colorIndex],
                    pointBackgroundColor: this.currentTheme[colorIndex],
                    hidden: this.legendItemsHidden.get(element.MetricID),
                    metricID: element.MetricID,
                });
            if (this.legendItemsHidden.get(element.MetricID)) { // if it has been previously hidden update the count of metrics tied to specific y-axis
                this.chartAxisShowStates[yAxisId] -= 1;
            }

            // Color Picker
            colorIndex++;
            if (colorIndex >= this.currentTheme.length) {
                colorIndex = 0;
            }
        }

        // Prep the x-axis labels
        // The server will fill values for all necessary labels. These are consistent for all series, so [0] will suffice.
        if (this.isListByGroup()) {
            xAxisLabels = this.data.GraphItems[0].GroupNames.slice();
        }
        else {
            xAxisLabels = this.data.GraphItems[0].IntervalGroups.slice(); // copy of the original

            // If it's a date type (i.e. "9/4/18"), get rid of the year from the label.
            // Types checked for are based off of the TimeInterval enum assigned in the controller.
            if ([2, 4, 5].indexOf(this.data.GraphItems[0].IntervalType) >= 0) { // If IntervalType is 2, 4, or 5
                for (let i = 0; i < xAxisLabels.length; i++) {
                    xAxisLabels[i] = moment(xAxisLabels[i], CVMomentDateFormat).format(CVMomentMonthDayFormat);
                }
            }

            //Check for x-axis date types to keep flat
            if ([2, 3, 4, 5].indexOf(this.data.GraphItems[0].IntervalType) >= 0) { // If IntervalType is 2, 3, 4, or 5
                this.chartMaximumRotation = 0;
            }
        }
        if (this.chartLegendPosition == null) {
            this.chartLegendPosition = (this.chartState.legendPosition != null) ? this.chartState.legendPosition : 'top';
        }
        this.verifyYAxisDisplay(preparedYAxes);

        //Create the chart
        let hovering = false;
        const tooltip = document.getElementsByClassName("legendTooltipInterval")[0];
        const tooltips = this.tooltipLegendItems;
        this.chartConfig = {
            //Horizontal bar chart is still a bar chart in chart.js so we need to set the type to bar
            type: (this.chartType === 'horizontalBar') ? 'bar' : this.chartType,
            data: {
                datasets: preppedDatasets,
                labels: xAxisLabels
            },
            options: { // Please keep option keys in alphabetical order for future searchability

                /* --- Single Value Options --- */
                maintainAspectRatio: false, //Disable to keep responsiveness, but stop pushing data off the bottom of the module pane
                responsive: true,
                spanGaps: false,            //Tells Chart.js to not draw lines between points when data is NaN/null.

                /* --- Object Based Options --- */
                animation: {
                    duration: (!this.firstAnimateComplete) ? 1000 : 0 /* If the first animation has not run, use the Chart.js default 1000, else set it to 0. */
                },
                elements: {
                    line: {
                        tension: 0.3
                    }
                },
                //This is used to set the bar chart to horizontal
                indexAxis: this.chartType === 'horizontalBar' ? 'y' : 'x',
                layout: {
                    padding: this.chartPadding
                },
                plugins: {
                    legend: {
                        onClick: this.legendClickHandler.bind(this),
                        display: this.showLegendPosition,
                        position: this.chartLegendPosition,
                        labels: {
                            boxWidth: 16,
                        },
                        onHover: (event, legendItem) => {
                            if (hovering || !tooltips.has(legendItem.datasetIndex)) {
                                return;
                            }
                            hovering = true;
                            tooltip.innerHTML = tooltips.get(legendItem.datasetIndex);
                            tooltip.setAttribute('style', `left:${event.x}px; right:${event.y}px; visibility:visible`);
                        },
                        onLeave: () => {
                            hovering = false;
                            tooltip.innerHTML = "";
                            tooltip.setAttribute('style', `visibility:hidden`);
                        },
                    },
                    title: {
                        display: false,
                        text: '',
                    },
                    tooltip: {
                        animation: false,
                        intersect: true,
                        mode: this.config.isCxOne ? 'point' : 'index',
                        position: 'nearest',
                        padding: this.config.isCxOne ? 12 : 6,
                        usePointStyle: true,
                        boxPadding: this.config.isCxOne ? 3 : 1,
                        boxWidth: this.config.isCxOne ? 11 : 18,
                        boxHeight: this.config.isCxOne ? 11 : 18,
                        borderColor: this.config.isCxOne ? '#D0D2D3' : '#353a40',
                        titleFont: {
                            size: this.config.isCxOne ? 12 : 16,
                            weight: this.config.isCxOne ? "normal" : "bold",
                        },
                        titleColor: this.config.isCxOne ? '#8c95a5' : '#fff',
                        bodyColor: this.config.isCxOne ? '#353a40' : '#fff',
                        footerColor: '#353a40',
                        backgroundColor: this.config.isCxOne ? '#FFF' : 'rgba(0, 0, 0, 0.8)',
                        bodyFont: {
                            size: 14,
                        },
                        footerFont: {
                            size: 14,
                            weight: 'bold',
                        },
                        borderWidth: this.config.isCxOne ? 1 : 0,
                        cornerRadius: this.config.isCxOne ? 0 : 6,
                        callbacks: {
                            // tooltipItem is an array of all Y-values for the given X-value/position being hovered over.
                            // tooltipItem should always have something at index [0] regardless of how many are in the series.
                            // So, get the series-data index of tooltipItem[0], which is used to pull the right x-value label.
                            title: tooltipItemArray => this.config.isCxOne
                                    ? this.getTooltipDateString(this.data.GraphItems[0].IntervalGroupsRaw[tooltipItemArray[0].dataIndex])
                                    : tooltipItemArray[0].label,
                            label: tooltipItem => {
                                const pmLabel = tooltipItem.dataset.label + ": " +
                                    (tooltipItem.dataset as any).displayValues[tooltipItem.dataIndex];
                                return this.config.isCxOne ? tooltipItem.dataset.label : pmLabel;
                            },
                            footer: tooltipItem => this.config.isCxOne ?
                                "     " + (tooltipItem[0].dataset as any).displayValues[tooltipItem[0].dataIndex] : "",
                            labelColor: tooltipItem => ({
                                borderColor: tooltipItem.dataset.borderColor,
                                backgroundColor: tooltipItem.dataset.backgroundColor,
                                borderWidth: this.config.isCxOne ? 0 : 0.5,
                            }),
                            labelPointStyle: context => ({
                                pointStyle: this.config.isCxOne ? 'circle' : 'rect',
                                rotation: 0,
                            }),
                        },
                    },
                },
                point: {
                    hitRadius: 4,
                    hoverRadius: 4,
                    pointRadius: 0
                },
                scales: {
                    //internally these are XAxes when working with horizontal bar charts
                    ...preparedYAxes
                },
            },
            plugins: [this.backgroundcolor]
        } as ChartConfiguration;

        const scale = {
            display: true,
            distribution: 'series',
            scaleLabel: {
                display: false,
            },
            ticks: {
                autoSkip: true,
                source: 'labels',
                maxRotation: this.chartMaximumRotation
            }
        }
        if (this.chartType === 'horizontalBar')
            this.chartConfig.options.scales.y = scale;
        else
            this.chartConfig.options.scales.x = scale;

            if (this.hideShowLegendClicked) {
                this.updateChartLegend();
                this.hideShowLegendClicked = false;
            }

        this.firstAnimateComplete = true;
        this.handleZoom();
    };

    getTooltipDateString(dateString: string): string {
        const date = new Date(dateString);

        const options: Intl.DateTimeFormatOptions = {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            timeZone: 'UTC'
        };
        const datePart = new Intl.DateTimeFormat('en-US', options).format(date);

        let hours = date.getUTCHours();
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const hourStr = hours.toString().padStart(2, '0');

        const timePart = `${hourStr}:${minutes} ${ampm}`;

        return `${datePart}, ${timePart}`;
    }

    updateChartData() {
        if (!this.data)
            return;

        this.chartConfig = this.createChart();
        // this.chartApi.canvas.parentElement.style.height = '100%';

        const data: any[] = [];
        const labels: string[] = [];

        this.data.BreakdownItems.forEach((value, key, map) => {
            data.push({x: moment(key, "YYYY-MM-DD"), y: value});
            labels.push(moment(key, "YYYY-MM-DD").format("MMM DD"));
        });

        this.chartConfig.data.datasets = [{
            data,
            borderColor: 'rgb(0,124,190)',
            fill: true,
            backgroundColor: 'rgba(0,124,190,0.1)',
            tension: 0.1,
            pointBackgroundColor: 'rgba(0, 124, 190, 1)',
            pointHoverBorderColor: 'rgba(0, 124, 190, 0.3)',
            pointHoverBorderWidth: 5
        }];
        this.chartConfig.data.labels = labels;

        this.handleZoom();

        this.animateUpdate = false;
    }

    /**
     * Updates the zoom level based on the deltaY value.
     * @param deltaY - The change in the Y direction.
     */
    updateZoomLevel(deltaY: number): void {
        const temp = this.zoom.cur + 0.1 * ((deltaY > 0) ? -1 : (deltaY < 0) ? 1 : 0);
        if (temp > this.zoom.max) {
            this.zoom.cur = this.zoom.max;
        } else if (temp < this.zoom.min) {
            this.zoom.cur = this.zoom.min;
        } else {
            this.zoom.cur = temp;
        }
    }
    
    /**
     * Updates the chart dimensions and ticks based on the provided `isHorizontalBar` flag.
     * @param isHorizontalBar - A boolean flag indicating whether the chart is a horizontal bar chart.
     */
    updateChartDimensionsAndTicks(isHorizontalBar: boolean): void {
        const axis = isHorizontalBar ? 'y' : 'x';
        const oldMaxTicks = this.chartConfig.options.scales[axis].ticks.maxTicksLimit;
        const dimension = isHorizontalBar ? 'height' : 'width';
        this.chartObject.chart.canvas.parentElement.style[dimension] = `max(100%,${100 * this.zoom.cur}%)`;
        this.chartConfig.options.scales[axis].ticks.maxTicksLimit = Math.round(this.zoom.cur * this.zoom.baseTicks);
    }
    
    /**
     * Handles the zoom functionality of the interval combo component.
     * @param deltaY - The change in the Y direction for zooming.
     */
    handleZoom(deltaY: number = 0): void {
        if (!this.data || !this.chartObject) {
            return;
        }
    
        this.updateZoomLevel(deltaY);
        this.updateChartDimensionsAndTicks(this.chartType === 'horizontalBar');
        this.chartObject.chart.update();
    }


    verifyYAxisDisplay(preparedYAxes) {
        for (const key in preparedYAxes) {
            if (this.chartAxisShowStates[key] === 0) {
                preparedYAxes[key].display = false;
            }
        }
    }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        this.modalRef = null;
        this.subscriptions.forEach(sub => {
            sub.unsubscribe();
        });
        clearTimeout(this.stateSaveTimeout);
        clearTimeout(this.resizeColsSaveStateTimeout);
        clearTimeout(this.focusOnSearchTimeout);
        document.removeEventListener('wheel', this.onMouseWheel);
        if (this.cancelRefresh) {
            this.cancelRefresh();
        }
    }

    determinePosition(numSeries: number): string {
        // 4 total types (enums: 0,1,2,3). The first two encountered are put on the left, the rest are put on the right for vertical bar; bottom then top for horizontal bar.
        if (this.chartType === 'horizontalBar')
            return numSeries <= 2 ? 'bottom' : 'top';
        else
            return numSeries <= 2 ? 'left' : 'right';
    }

    exportPNG() {
        // toBase64Image() returns a png data url of the image on the canvas. (Sourced: https://www.chartjs.org/docs/latest/developers/api.html)
        // name of the file
        const date = (new Date()).toString('yyyy_MM_dd_HH_mm_ss');
        const fileName = this.cvLocalizationService.getCachedText("Module_Names","CXone_ACDIntervalBreakdown","CXone_ACDIntervalBreakdown")
            .replace(/\s+/g, '-').toLowerCase() + '_widget_(' + date + ').png';

        // create fake link
        const link = document.createElement('a');
        link.setAttribute('download', fileName);
        link.setAttribute('href', this.chartObject?.chart.toBase64Image());

        // start download
        link.click();

        // cleanup
        URL.revokeObjectURL(link.href);
        link.remove();
    }

    // must be called after getModuleThemesMenu which populates this.themeMenuOptions
    setThemeClick() {
        //Set the index, get the theme, update the chart
        for (const theme of this.themeMenuOptions) {
            theme.click = () => {
                this.currentThemeIndex = theme.themeIndex;
                this.currentTheme = this.moduleThemesService.GetThemeByIndex(this.currentThemeIndex);
                this.createChart();

                //Update the state
                this.chartState.themeIndex = this.currentThemeIndex;
                this.queueStateSave();
            };
        }
    }

    createBreakdownGroupsGrid(moduleData: IntervalModuleData): GridOptions {
        if (!this.isFirstLoad && this.gridStateLoaded)
            this.gridState = null;
        
        const gridOptions = this.defaultGridOptions((gridReadyEvent: GridReadyEvent) => {
            this.updateBreakdownGrid(gridReadyEvent.api, moduleData);
        });

        gridOptions.components = {
            ...this.defaultGridOptions().components,
            groupRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
            groupHeaderComponent: this.agGridHelperService.GroupHeaderComp,
        };

        gridOptions.columnDefs = [
            {
                field: "GroupData",
                colId: "Group",
                type: "groupColumn",
                keyCreator: params => params?.value.groupID,
                rowGroup: true,
                hide: true,
                cellRenderer: 'agGroupCellRenderer',
                // pinnedRowCellRenderer: () => this.cvLocalizationService.getCachedText("Interval_Module", "Totals", "Totals"),
                cellRendererSelector: params => {
                    // TODO: Ag Grid Update 30.0.0 - pinnedRowCellRenderer is deprecated. 'Use cellRendererSelector instead'. This implementation may not work though
                    if (params.node.rowPinned) {
                        return {
                            component: () => this.isCXOne ? this.cxoneTotalText : (!this.showTotal ? this.avgsText : this.totalsText),
                            params: {
                                suppressCount: true,
                            },
                        };
                    }
                },
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: 'groupRenderer',
                    innerRendererParams: {
                        groupedRowData: value => {
                            const item = this.data?.BreakdownItems?.find(item => item.GroupData?.groupID === value?.groupID);
                            return item?.GroupData || {};
                        },
                        isIntervalListGroupRow: true,  //tells group cell renderer that this is the interval's grouped rows that will show group name instead of time interval
                    }
                },
                filterParams: {
                    getValue: (node: RowNode) => (node?.data?.GroupData as ModuleDataGroupItem)?.groupName,
                },
                suppressMenu: this.isSharedURL,
            },
        ];

        const metricIDToMetric = new Map<string, Metric>();

        moduleData.Metrics?.forEach(metric => {
            const metricID = metric.MetricID;
            const valueGetter = (node: IRowNode) => (node?.data as BreakdownItem)?.AllMetricData[metricID];
            gridOptions.columnDefs.push({
                colId: metricID.toString(),
                valueGetter: params => valueGetter(params.node),
                headerName: (metric.MetricCustomName) ? metric.MetricCustomName + "*" : metric.Abbreviation,
                type: "metricColumn",
                cellRenderer: 'metricMenuRenderer',
                cellRendererParams: {isCXOne: this.config.isCxOne,
                    groupLevel: this.data.BreakdownItems[0]?.GroupData.groupLevel,
                    groupType: this.data.BreakdownItems[0]?.GroupData.groupType
                },
                aggFunc: 'breakdownGroupAgg',
                filterParams: {
                    getValue: (node: RowNode) => valueGetter(node)?.MetricValue,
                },
                suppressMenu: this.isSharedURL,
                
            });
            metricIDToMetric.set(metricID.toString(), metric);
        });

        gridOptions.context.getGroupData =
            params => this.data.BreakdownItems.find(item => item.GroupData.groupID.toString() === params.node.key)?.GroupData ||
                    params.node?.data?.GroupData;
        gridOptions.context.getMetric = params => metricIDToMetric.get(params.colDef.colId);
        gridOptions.getRowId = ({ data }) => `${data.Interval}_${data.GroupData.groupID}`;
        gridOptions.groupDefaultExpanded = -1;
        gridOptions.suppressAggFuncInHeader = true; // If the metric is called "Calls", AggFuncInHeader displays it as "func(Call)". Suppress this function string.
        gridOptions.autoGroupColumnDef = {
            field: "Interval",
            pinned: "left",
            type: "groupColumn",
            cellRendererParams: {
                suppressCount: true, // This suppresses the row group header's count of the number of rows in the group.
                suppressPadding: true,
                innerRendererParams: {
                    isIntervalListGroupRow: true, //tells group cell renderer that the interval is broken down by group
                    groupedRowData: value => this.data.BreakdownItems.find(item => item.GroupData.groupID === value.groupID).GroupData,
                }
            },
            headerComponent: "groupHeaderComponent",
            cellClass: 'ag-left-aligned-cell',
            comparator: (nodeA, nodeB): number => {
                if (nodeA && nodeB) {
                    try {
                        const nodeAInterval = Date.parse(nodeA);
                        const nodeBInterval = Date.parse(nodeB);
                        return nodeAInterval.compareTo(nodeBInterval);
                    }
                    catch {
                        return 0;
                    }
                }
                else return 0;
            }
        };

        /* CXCV-1090 - Colton: This is needed to allow groups to sort by default when there is no sorting in place. Prior to this,
            the new intervals would get appended to the bottom. So if the existing grouping was by hour (12:00 -> 1:00 -> 2:00) and
            it was switched to half hour, it would be 12:00 -> 1:00....11:00 -> 12:30. This comparator reorganizes the groups so the sub-hour intervals
            are put in place where they belong: 12:00 -> 12:30 -> 1:00.
        */
        gridOptions.initialGroupOrderComparator = ({nodeA, nodeB}): number => {
            if (nodeA && nodeB) {
                try {
                    const nodeAInterval = Date.parse(nodeA.key);
                    const nodeBInterval = Date.parse(nodeB.key);
                    return nodeAInterval.compareTo(nodeBInterval);
                }
                catch {
                    return 0;
                }
            }
            else return 0;
        };
        return this.agGridHelperService.createGridOptions(gridOptions, {
            enableCopy: true
        });
    }

    private isListByGroup() {
        return this.data.ViewMode === TableViewMode.Breakdown && !this.data.ListByInterval;
    }
}
